/**
 * `Chip` — the rectangular filter chip. `docs/design-direction.md` §9.7.
 *
 * 1px `--border` on `--bg-raised`, guillotine-cut (`--r-cut`), mono caps `--fs-11`,
 * **32px visual height inside a 44px hit row**: the slop is a pseudo-element
 * expander, never a margin, so a dense filter strip stays dense while every target
 * stays thumbable (§5.6). Selected inverts to the cream ground with knocked-out ink —
 * never a pill, never a fill with a rounded corner (§9.7, §5.3).
 *
 * Where it appears: the horizontally scrolling filter strip on the crate (`ГОД`,
 * `РИДДИМ`, `ЛЕЙБЛ`, `BPM`, `7" / 12"`) and the transport's secondary controls
 * (`SHUFFLE`, `REPEAT`, `SPEED`, `VERSION`), which fill solid `--accent` with
 * knocked-out type when active — that fill is the transport's own state, applied by
 * `Transport`, because a filter chip and a latched control are the same object with
 * different ink.
 *
 * **A toggle carries `aria-pressed`.** The chip is a real `<button>` when it has an
 * `onToggle`, and a plain marker when it does not (a static `SIDE A` label in a spec
 * strip, for instance) — a control that cannot be pressed must not claim it can.
 *
 * Cyrillic copy keeps the tier but loses the tracking: `ГОД` set at +0.16em is
 * shouting, and tracked uppercase Cyrillic is banned (§4.3, §12.16).
 *
 * No haptics here. `impactOccurred('light')` on a chip toggle (§10.1) belongs to the
 * screen that owns the interaction: a primitive that reaches for
 * `window.Telegram.WebApp` stops being presentational and starts being untestable.
 */

import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './primitives.module.css';
import { cx } from './Rule';

export interface ChipProps {
  /** The printed label. */
  readonly label: string;
  /** Selected chips invert to the paper ground with `--ink-000` type. */
  readonly selected?: boolean;
  readonly disabled?: boolean;
  /** Omit for a static marker: without it the chip is not a control at all. */
  readonly onToggle?: (() => void) | undefined;
  readonly className?: string | undefined;
}

export function Chip({
  label,
  selected = false,
  disabled = false,
  onToggle,
  className,
}: ChipProps): JSX.Element {
  const classes = cx(
    styles.chip,
    't-mono',
    't-11',
    hasCyrillic(label) && styles.cyr,
    onToggle !== undefined && styles.expand,
    className,
  );

  if (onToggle === undefined) {
    return (
      <span className={classes} data-selected={selected ? 'true' : 'false'}>
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      data-selected={selected ? 'true' : 'false'}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onToggle}
    >
      {label}
    </button>
  );
}
